import React, { useState } from 'react';

export interface SnsTopicArnProps {
  arn?: string;
  label?: string;
  className?: string;
  /** Indicates SNS topic creation is in progress */
  isCreating?: boolean;
}

/**
 * SNS Topic ARN display component with copy-to-clipboard functionality.
 *
 * Displays the task's SNS topic ARN with a copy button, allowing value streams
 * to easily subscribe to file notifications (FR-005).
 */
export function SnsTopicArn({
  arn,
  label = 'SNS Topic ARN',
  className = '',
  isCreating = false
}: SnsTopicArnProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!arn) return;

    try {
      await navigator.clipboard.writeText(arn);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = arn;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Show creating state with spinner
  if (isCreating) {
    return (
      <div className={`mb-4 ${className}`}>
        <label className="
          block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1.5
        ">
          {label}
        </label>
        <div className="
          flex items-center gap-2
          bg-primary-50 dark:bg-primary-900/20
          border border-primary-300 dark:border-primary-700
          rounded-md px-3 py-2
        ">
          <span className="flex items-center justify-center text-primary-600 dark:text-primary-400" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="animate-spin">
              <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/>
            </svg>
          </span>
          <span className="text-[13px] text-primary-600 dark:text-primary-400">
            Creating SNS topic...
          </span>
        </div>
      </div>
    );
  }

  if (!arn) {
    return (
      <div className={`mb-4 ${className}`}>
        <label className="
          block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1.5
        ">
          {label}
        </label>
        <div className="
          flex items-center gap-2
          bg-warning-50 dark:bg-warning-900/20
          border border-warning-300 dark:border-warning-700
          rounded-md px-3 py-2
        ">
          <span className="text-[13px] text-warning-700 dark:text-warning-400 italic">
            SNS topic will be created when task is enabled
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`mb-4 ${className}`}>
      <label className="
        block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1.5
      ">
        {label}
      </label>
      <div className="
        flex items-center gap-2
        bg-neutral-100 dark:bg-slate-800
        border border-neutral-200 dark:border-slate-700
        rounded-md px-3 py-2
      ">
        <code className="
          flex-1 font-mono text-[13px]
          text-neutral-800 dark:text-neutral-200
          break-all overflow-x-auto whitespace-nowrap max-w-full
        " title={arn}>
          {arn}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className={`
            flex-shrink-0 flex items-center justify-center
            w-8 h-8 p-0 border-0 rounded
            bg-transparent cursor-pointer
            transition-all duration-150
            ${copied
              ? 'text-success-600 dark:text-success-400 hover:text-success-600 dark:hover:text-success-400'
              : 'text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200 dark:hover:bg-slate-700 hover:text-neutral-800 dark:hover:text-neutral-200'
            }
            focus:outline-none focus:ring-2 focus:ring-primary-500
          `}
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          aria-label={copied ? 'Copied to clipboard' : 'Copy ARN to clipboard'}
        >
          {copied ? (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
          )}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500">
        Subscribe to this topic to receive file notifications
      </p>
    </div>
  );
}

export default SnsTopicArn;
